/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { EMPTY_TABLE, isDirty, Table } from '@gh/common-shared';
import { EMPTY_ID, ID, WithDirty, WithFormMode, WithVersion } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { first } from 'lodash';
import {
  getPbLinkCostCenterId,
  PbLinkCcElement,
  pbLinkCcElementTable,
  pbLinkCcTable,
  PbLinkCostCenter,
  PbLinkCsm,
  PbLinkCsmGetResponse,
  PbLinkCsmHeading,
  PbLinkCsmItem,
  PbLinkCsmRecord,
  PbLinkCsmSubheading,
  PbLinkEntryPoint,
  PbLinkGetResponse,
  PbLinkJst,
  PbLinkJstParagraph,
  PbLinkJstRecord,
  PbLinkJstSection,
  pbLinkRecordTable,
} from '../../resources/price-book-link.model';

export enum PriceBookLinkStateEventType {
  Load = 'Load',
  LoadCostCenter = 'LoadCostCenter',
  SelectCostCenter = 'SelectCostCenter',
  SelectElement = 'SelectElement',
  ResetRecordFocusedCell = 'ResetRecordFocusedCell',
  NavigateToInvalid = 'NavigateToInvalid',
  SetRecordField = 'SetRecordField',
  ResetDirty = 'ResetDirty',
  ToggleShowDeleted = 'ToggleShowDeleted',
}

export type PriceBookLinkStateLoadEvent = {
  type: PriceBookLinkStateEventType.Load;
  init: PbLinkGetResponse;
  mode: FormMode;
};
export type PriceBookLinkCsmStateLoadEvent = {
  type: PriceBookLinkStateEventType.Load;
  init: PbLinkCsmGetResponse;
  mode: FormMode;
};
export type PriceBookLinkStateSelectCostCenterEvent = {
  type: PriceBookLinkStateEventType.SelectCostCenter;
  costCenterId: ID;
};
export type PriceBookLinkStateSelectElementEvent = {
  type: PriceBookLinkStateEventType.SelectElement;
  elementId: ID;
};
export type PriceBookLinkStateResetRecordFocusedCellEvent = {
  type: PriceBookLinkStateEventType.ResetRecordFocusedCell;
};
export type PriceBookLinkStateNavigateToInvalidEvent = {
  type: PriceBookLinkStateEventType.NavigateToInvalid;
};
export type PriceBookLinkStateLoadElementsEvent = {
  type: PriceBookLinkStateEventType.LoadCostCenter;
  costCenterId: ID;
  elements: PbLinkCcElement[];
};
export type PriceBookLinkStateSetRecordFieldEvent = {
  type: PriceBookLinkStateEventType.SetRecordField;
  recordId: ID;
  name: keyof PbLinkJstRecord;
  value: any;
};
export type PriceBookLinkStateResetDirtyEvent = {
  type: PriceBookLinkStateEventType.ResetDirty;
};
export type PriceBookLinkStateToggleShowDeletedEvent = {
  type: PriceBookLinkStateEventType.ToggleShowDeleted;
};

export type PriceBookLinkStateEvent = PriceBookLinkStateLoadEvent
  | PriceBookLinkStateSelectCostCenterEvent | PriceBookLinkStateSelectElementEvent
  | PriceBookLinkStateResetRecordFocusedCellEvent | PriceBookLinkStateNavigateToInvalidEvent
  | PriceBookLinkStateLoadElementsEvent
  | PriceBookLinkStateSetRecordFieldEvent
  | PriceBookLinkStateToggleShowDeletedEvent
  | PriceBookLinkStateResetDirtyEvent;

export type PriceBookLinkJstMasterInfo = {
  priceBookName: string;
};

export type PriceBookLinkJstSelection = {
  costCenterId?: ID;
  elementId?: ID;
  recordFocusedCell?: FocusedCell;
};

export type PriceBookLinkFilter = {
  isShowDeleted: boolean;
};

export type PriceBookLinkCommonState = WithVersion<WithFormMode<WithDirty<{
  entryPoint: PbLinkEntryPoint;
  priceBookId: ID;
  // common information about price book and its links
  masterInfo: PriceBookLinkJstMasterInfo;
  // all cost centers
  costCenters: Table<PbLinkCostCenter>;
  // loaded elements
  elements: Table<PbLinkCcElement>;
  // the following object holds current selection
  selection: PriceBookLinkJstSelection;
  filter: PriceBookLinkFilter;
}>>>;

export type PriceBookLinkJstState = PriceBookLinkCommonState & {
  // records for currently selected element
  currentRecords: Table<PbLinkJstRecord>;
  // all available templates
  templates: Hash<PbLinkJst>;
  // sections of all available templates
  sections: Hash<PbLinkJstSection>;
  // paragraphs of all available templates
  paragraphs: Hash<PbLinkJstParagraph>;
  // mapping of sections to paragraphs
  sectionToParagraph: Hash<PbLinkJstParagraph>;
};

export type PriceBookLinkCsmState = PriceBookLinkCommonState & {
  // records for currently selected element
  currentRecords: Table<PbLinkCsmRecord>;
  // all available masters
  masters: Hash<PbLinkCsm>;
  // headings of all available masters
  headings: Hash<PbLinkCsmHeading>;
  // subheadings of all available masters
  subheadings: Hash<PbLinkCsmSubheading>;
  // items of all available masters
  items: Hash<PbLinkCsmItem>;
  // mapping of item to subheadings
  itemToSubheading: Hash<PbLinkCsmSubheading>;
  // mapping of subheading to headings
  subheadingToHeading: Hash<PbLinkCsmHeading>;
};

export type PriceBookLinkState = PriceBookLinkJstState | PriceBookLinkCsmState;

export const PRICE_BOOK_LINK_JST_INIT_STATE: PriceBookLinkJstState = {
  entryPoint: PbLinkEntryPoint.Jst,
  priceBookId: EMPTY_ID,
  formMode: FormMode.EditMode,
  masterInfo: { priceBookName: '' },
  costCenters: EMPTY_TABLE,
  elements: EMPTY_TABLE,
  currentRecords: EMPTY_TABLE,
  templates: {},
  paragraphs: {},
  sections: {},
  sectionToParagraph: {},
  isDirty: false,
  filter: { isShowDeleted: false },
  selection: {},
};

export const PRICE_BOOK_LINK_CSM_INIT_STATE: PriceBookLinkCsmState = {
  entryPoint: PbLinkEntryPoint.Csm,
  priceBookId: EMPTY_ID,
  formMode: FormMode.EditMode,
  masterInfo: { priceBookName: '' },
  costCenters: EMPTY_TABLE,
  elements: EMPTY_TABLE,
  currentRecords: EMPTY_TABLE,
  masters: {},
  headings: {},
  subheadings: {},
  items: {},
  itemToSubheading: {},
  subheadingToHeading: {},
  isDirty: false,
  filter: { isShowDeleted: false },
  selection: {},
};

export const queryPbLinkEntryPoint = ({ entryPoint }: PriceBookLinkState) => entryPoint;
export const queryPbLinkId = ({ priceBookId }: PriceBookLinkState) => priceBookId;
export const queryPbLinkPriceBookName = ({ masterInfo: { priceBookName } }: PriceBookLinkState) => priceBookName;

export const queryPbLinkJst = ({ templates }: PriceBookLinkJstState, id: ID) => templates[id];
export const queryPbLinkJstParagraph = ({ paragraphs }: PriceBookLinkJstState, id: ID) => paragraphs[id];
export const queryPbLinkJstSection = ({ sections }: PriceBookLinkJstState, id: ID) => sections[id];
export const queryPbLinkJstParagraphBySection = ({ sectionToParagraph }: PriceBookLinkJstState, id: ID) =>
  sectionToParagraph[id];

export const queryPbLinkCsm = ({ masters }: PriceBookLinkCsmState, id: ID) => masters[id];
export const queryPbLinkCsmHeading = ({ headings }: PriceBookLinkCsmState, id: ID) => headings[id];
export const queryPbLinkCsmSubheading = ({ subheadings }: PriceBookLinkCsmState, id: ID) => subheadings[id];
export const queryPbLinkCsmItem = ({ items }: PriceBookLinkCsmState, id: ID) => items[id];
export const queryPbLinkCsmSubheadingByItem = ({ itemToSubheading }: PriceBookLinkCsmState, id: ID) =>
  itemToSubheading[id];
export const queryPbLinkCsmHeadingBySubheading = ({ subheadingToHeading }: PriceBookLinkCsmState, id: ID) =>
  subheadingToHeading[id];

export const queryPbLinkCcInvalidElements = (state: PriceBookLinkState, costCenterId: ID) =>
  pbLinkCcElementTable.byGroup(state.elements, 'costCenterErrors', costCenterId);
export const queryPbLinkHasErrors = (state: PriceBookLinkState) =>
  queryPbLinkCostCenters(state).some((cc) => queryPbLinkCcInvalidElements(state, getPbLinkCostCenterId(cc)).length > 0);

export const queryPbLinkDirtyElements = (state: PriceBookLinkState) =>
  pbLinkCcElementTable.all(state.elements).filter(isDirty);

export const queryPbLinkCostCenters = (state: PriceBookLinkState) =>
  pbLinkCcTable.byOrder(state.costCenters, 'code');
export const queryPbLinkFirstCostCenter = (state: PriceBookLinkState) => first(queryPbLinkCostCenters(state));
export const queryPbLinkCcElements = (state: PriceBookLinkState, costCenterId: ID) =>
  pbLinkCcElementTable.byGroup(state.elements, 'costCenter', costCenterId);
export const queryPbLinkFirstCcElement = (state: PriceBookLinkState, costCenterId: ID) =>
  first(queryPbLinkCcElements(state, costCenterId));
export const queryPbLinkCostCenter = (state: PriceBookLinkState, costCenterId: ID) =>
  pbLinkCcTable.byId(state.costCenters, costCenterId);
export const queryPbLinkCcElement = (state: PriceBookLinkState, elementId: ID) =>
  pbLinkCcElementTable.byId(state.elements, elementId);
export const queryPbLinkCurrentRecords = (state: PriceBookLinkState) =>
  pbLinkRecordTable.byOrder(state.currentRecords, 'name');
export const queryPbLinkCurrentVisibleRecords = (state: PriceBookLinkState, isShowDeleted: boolean) => {
  const currentRecords = queryPbLinkCurrentRecords(state);
  return isShowDeleted ? currentRecords : currentRecords.filter((record) => record.isActive);
};
export const queryPbLinkSelectedElement = (state: PriceBookLinkState) => {
  const elementId = queryPbLinkSelectedElementId(state);
  return elementId ? queryPbLinkCcElement(state, elementId) : void 0;
};
export const queryPbLinkSelectedCostCenter = (state: PriceBookLinkState) => {
  const costCenterId = queryPbLinkSelectedCostCenterId(state);
  return costCenterId ? queryPbLinkCostCenter(state, costCenterId) : void 0;
};

export const queryPbLinkSelectedCostCenterId = (state: PriceBookLinkState) => state.selection.costCenterId;
export const queryPbLinkSelectedElementId = (state: PriceBookLinkState) => state.selection.elementId;
export const queryPbLinkRecordFocusedCell = (state: PriceBookLinkState) => state.selection.recordFocusedCell;

export const queryPbLinkSaveModel = queryPbLinkDirtyElements;

export const queryPbLinkShowDeleted = (state: PriceBookLinkState) => state.filter.isShowDeleted;
